import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';

import { apiQuery } from '../../query';

import {
  NotificationSetting,
  NotificationRequest,
  NotificationResponse,
  SaveNotificationSettingsRequest,
} from './types';

const tagTypes = ['notifications'];

export const notificationsApi = createApi({
  tagTypes,
  baseQuery: apiQuery,
  reducerPath: 'notificationsApi',
  endpoints: (build) => ({
    markAllUnread: build.mutation<void, void>({
      invalidatesTags: tagTypes,
      query: () => ({
        method: 'post',
        url: URL.MARK_ALL_AS_READ_NOTIFICATIONS,
      }),
    }),

    settings: build.query<NotificationSetting[], void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.GET_NOTIFICATIONS_SETTINGS,
      }),
    }),

    markAsRead: build.mutation<void, number>({
      invalidatesTags: tagTypes,
      query: (id) => ({
        method: 'post',
        url: generatePath(URL.MARK_AS_READ_NOTIFICATION, { id }),
      }),
    }),

    saveSettings: build.mutation<
      NotificationSetting[],
      SaveNotificationSettingsRequest
    >({
      invalidatesTags: tagTypes,
      query: (data) => ({
        data,
        method: 'post',
        url: URL.SAVE_NOTIFICATIONS_SETTINGS,
      }),
    }),

    notifications: build.query<
      NotificationResponse,
      NotificationRequest | void
    >({
      providesTags: tagTypes,
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };

        return {
          method: 'get',
          url: addParamsToUrl(URL.GET_NOTIFICATIONS, searchParams),
        };
      },
    }),
  }),
});

export const {
  useSettingsQuery,
  useMarkAsReadMutation,
  useNotificationsQuery,
  useSaveSettingsMutation,
  useMarkAllUnreadMutation,
} = notificationsApi;

export type { NotificationSetting, SaveNotificationSettingsRequest };
